/**
 * jw-product-context.js
 * Phase F2.1 — Unified Theme Runtime Context Factory
 *
 * 职责：将 raw Shopify/Liquid data 归一化为 JWProductContext
 *
 * 输入：window.__PD 或 preRenderedData
 * 输出：window.JWProductContext.create(raw, opts) → frozen context
 *
 * 🚫 禁止：
 *   - 解析图片 URL
 *   - 计算价格
 *   - 修改 variant 数据
 *   - 业务逻辑判断
 *
 * Contract: docs/contracts/product_context_v1.md
 */
(function () {
  'use strict';

  /**
   * Build O(1) SKU lookup map from variant list.
   * Key format: option values joined by "::", empty values stripped.
   */
  function buildSkuMap(list) {
    var map = {};
    if (!Array.isArray(list)) return map;
    list.forEach(function (v) {
      var raw = v.options;
      if (!Array.isArray(raw)) return;
      var opts = [];
      for (var i = 0; i < raw.length; i++) {
        // 兼容两种格式: 字符串数组 vs 对象数组
        var val = (typeof raw[i] === 'object' && raw[i] !== null) ? raw[i].value : raw[i];
        val = (val === null || val === undefined) ? '' : String(val).trim();
        if (val) opts.push(val);
      }
      if (opts.length > 0) {
        map[opts.join('::')] = v;
      }
    });
    return map;
  }

  /**
   * Build O(1) SKU lookup by ID (e.g. "SKU-001" → variant).
   * F2.3: used by jw-product-variant.js for variant domain lookups.
   */
  function buildByIdMap(list) {
    var map = {};
    if (!Array.isArray(list)) return map;
    list.forEach(function (v) {
      if (v.id) map[String(v.id)] = v;
    });
    return map;
  }

  /**
   * Normalize tiers to {qty: cents} flat object.
   * Handles: priceTiersRaw (flat), firstTierTiers (array), prices (preRenderedData).
   */
  function normalizeTiers(raw) {
    var tiers = {};
    if (!raw) return tiers;

    // 1. priceTiersRaw — Compiler-generated flat object {qty: price_cents}
    var ptr = raw.priceTiersRaw;
    if (ptr && typeof ptr === 'object' && !Array.isArray(ptr)) {
      var keys = Object.keys(ptr);
      for (var i = 0; i < keys.length; i++) {
        tiers[keys[i]] = Number(ptr[keys[i]]) || 0;
      }
      if (Object.keys(tiers).length > 0) return tiers;
    }

    // 2. firstTierTiers — [{min, price}] array
    if (Array.isArray(raw.firstTierTiers) && raw.firstTierTiers.length > 0) {
      raw.firstTierTiers.forEach(function (t) {
        tiers[String(t.min || t.qty || 1)] = Number(t.price) || 0;
      });
      return tiers;
    }

    // 3. prices — preRenderedData flat object
    var pr = raw.prices;
    if (pr && typeof pr === 'object' && !Array.isArray(pr)) {
      var keys2 = Object.keys(pr);
      for (var j = 0; j < keys2.length; j++) {
        tiers[keys2[j]] = Number(pr[keys2[j]]) || 0;
      }
    }

    return tiers;
  }

  /**
   * Normalize variant list from raw.__PD or raw.variants.
   * Output: unified {id, options:[], image, price, compare_at_price} format.
   */
  function normalizeVariantList(raw) {
    if (!raw) return [];

    var list = raw.skus || raw.variants || [];
    if (!Array.isArray(list)) return [];
    if (list.length === 0) return [];

    // Detect format: PD.skus has native_price_cents or price_base
    var first = list[0];
    var isSkuFormat = (first && (first.native_price_cents !== undefined || first.price_base !== undefined));

    return list.map(function (v) {
      if (isSkuFormat) {
        return {
          id: String(v.id || v.sku || ''),
          options: Array.isArray(v.options) ? v.options.slice() : [],
          image: v.img || v.thumb || v.image || '',
          price: Number(v.native_price_cents) || Math.round((Number(v.price_base) || 0) * 100),
          compare_at_price: Number(v.compare_at_cents) || 0
        };
      }
      // Raw Shopify variant
      return {
        id: String(v.id || ''),
        options: [v.option1 || '', v.option2 || '', v.option3 || ''],
        image: (v.featured_image && v.featured_image.src) || (v.image && v.image.src) || '',
        price: Number(v.price) || 0,
        compare_at_price: Number(v.compare_at_price) || 0
      };
    });
  }

  /**
   * JWProductContext.create(raw, opts)
   *
   * @param {object} raw  — window.__PD | preRenderedData
   * @param {object} opts — {lite: boolean} (optional)
   * @returns {object}    — ProductContext 对象
   */
  function create(raw, opts) {
    var d   = raw || {};
    var lite = !!(opts && opts.lite);
    var prod = d.product || {};

    var variantList = normalizeVariantList(d);

    return {
      identity: {
        global_product_id: d.global_product_id || null,
        source: prod.vendor || d.vendor || null
      },

      product: {
        id:     String(prod.id || d.product_id || ''),
        handle: d.productHandle || '',
        title:  prod.title || d.title || '',
        tags:   Array.isArray(prod.tags) ? prod.tags.slice()
                  : (Array.isArray(d.tags) ? d.tags.slice() : []),
        moq: Number(d.moq) || 1
      },

      images: {
        all:    lite ? []
                  : (Array.isArray(d._allImages) ? d._allImages.slice()
                    : (Array.isArray(d.mainImages) ? d.mainImages.slice() : [])),
        main:   lite ? []
                  : (Array.isArray(d.mainImages) ? d.mainImages.slice() : []),
        detail: lite ? []
                  : (Array.isArray(d.detailImages) ? d.detailImages.slice() : []),
        skuMap: (d.skuImages && typeof d.skuImages === 'object' && !Array.isArray(d.skuImages))
                  ? Object.assign({}, d.skuImages) : {}
      },

      variants: {
        list:   variantList,
        skuMap: buildSkuMap(variantList),
        byId:   buildByIdMap(variantList)
      },

      pricing: {
        tiers: normalizeTiers(d)
      },

      binding: {
        imageBinding: d.imageBinding || null
      }
    };
  }

  // ── 暴露到 window ──
  window.JWProductContext = { create: create };

})();
